import { useEffect, useRef } from "react";
import {
	AreaSeries,
	createChart,
	type IChartApi,
	type ISeriesApi,
	type UTCTimestamp,
} from "lightweight-charts";

import type { Bar, ChartRange } from "../lib/api/market";

interface StockChartProps {
	bars: Bar[];
	range: ChartRange;
	height?: number;
	loading?: boolean;
}

const DEFAULT_HEIGHT = 280;

// AreaSeries chart for the stock detail page. TradingView's lightweight-charts
// (MIT-ish via Apache-2.0, ~50KB gzipped) is the de-facto standard for this
// kind of finance UI — zero runtime deps and a small canvas-based core means
// it doesn't bloat the main bundle the way Recharts or a D3-built chart
// would.
//
// We deliberately keep the API surface narrow:
//   - bars: already-normalized OHLC payload
//   - range: only used for x-axis tick formatting (intraday vs. multi-year
//     need different formats)
//   - loading + empty render their own skeleton/empty states so the parent
//     route doesn't have to special-case the chart container size on mount
//
// The chart re-creates from scratch whenever the bars array identity changes.
// That's wasteful in principle but bars come over the network once per
// range-tab click, and tearing-down + rebuilding takes <1ms for the sizes we
// render, so it's not worth optimizing.

export function StockChart({
	bars,
	range,
	height = DEFAULT_HEIGHT,
	loading = false,
}: StockChartProps) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const seriesRef = useRef<ISeriesApi<"Area"> | null>(null);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		if (loading || bars.length === 0) return;

		const accent = readCssVar("--color-accent") ?? "#10b981";
		const muted = readCssVar("--color-muted") ?? "#a1a1aa";
		const border = readCssVar("--color-border") ?? "#21262d";

		const chart = createChart(container, {
			height,
			autoSize: true,
			layout: {
				background: { color: "transparent" },
				textColor: muted,
				fontFamily:
					"Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
				fontSize: 11,
			},
			grid: {
				vertLines: { color: "transparent" },
				horzLines: { color: border, style: 0 },
			},
			rightPriceScale: {
				borderColor: border,
				scaleMargins: { top: 0.1, bottom: 0.1 },
			},
			timeScale: {
				borderColor: border,
				timeVisible: range === "1D" || range === "1W",
				secondsVisible: false,
			},
			crosshair: {
				mode: 1, // Magnet — snaps the crosshair to nearest bar.
				vertLine: {
					color: accent,
					width: 1,
					style: 3,
					labelBackgroundColor: accent,
				},
				horzLine: {
					color: accent,
					width: 1,
					style: 3,
					labelBackgroundColor: accent,
				},
			},
			handleScale: false,
			handleScroll: false,
		});
		chartRef.current = chart;

		const series = chart.addSeries(AreaSeries, {
			lineColor: accent,
			topColor: hexToRgba(accent, 0.32),
			bottomColor: hexToRgba(accent, 0.0),
			lineWidth: 2,
			priceLineVisible: false,
			lastValueVisible: false,
			crosshairMarkerRadius: 4,
		});
		seriesRef.current = series;

		series.setData(
			bars
				.map((b) => ({
					time: Math.floor(new Date(b.time).getTime() / 1000) as UTCTimestamp,
					value: b.close,
				}))
				// lightweight-charts requires strictly ascending timestamps.
				.sort((a, b) => (a.time as number) - (b.time as number)),
		);

		chart.timeScale().fitContent();

		return () => {
			chart.remove();
			chartRef.current = null;
			seriesRef.current = null;
		};
	}, [bars, range, height, loading]);

	if (loading) {
		return (
			<div
				role="status"
				aria-label="Loading chart"
				className="animate-pulse rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]"
				style={{ height }}
			/>
		);
	}

	if (bars.length === 0) {
		return (
			<div
				className="flex items-center justify-center rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-muted)]"
				style={{ height }}
			>
				No price history available.
			</div>
		);
	}

	return (
		<div
			ref={containerRef}
			data-testid="stock-chart"
			className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]"
			style={{ height }}
		/>
	);
}

// --- helpers ---------------------------------------------------------------

function readCssVar(name: string): string | null {
	if (typeof window === "undefined") return null;
	const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
	return v.length > 0 ? v : null;
}

/**
 * Convert a hex color (#rrggbb) to an rgba() string with the given alpha.
 * Falls back to the input if parsing fails so the chart still renders.
 */
function hexToRgba(hex: string, alpha: number): string {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
	if (!m) return hex;
	const intVal = parseInt(m[1], 16);
	const r = (intVal >> 16) & 0xff;
	const g = (intVal >> 8) & 0xff;
	const b = intVal & 0xff;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
